import React from 'react';
import { clampWordScale, fitScaleToWidth, transformText, typewriterTimeline, wordBidiClass } from '../../shared/captionLayout';
import { toSeconds as defaultToSeconds, activeWordPillStyle, activeWordPillAngle, activeWordPillLift, captionWordTileStyle, captionWordTileDrop } from '../../shared/captionStyles';

/**
 * Renders the *body* of a caption that `layoutCaption()` has already placed: the stagger
 * staircase, the typewriter, the karaoke flow lines, or plain text. The caller owns the
 * positioning wrapper and the box chrome around it, because the editor draws selection outlines
 * and drag affordances on that element while the landing hero does not.
 *
 * Everything editor-specific arrives through `decorations` and is inert when omitted, so the
 * landing hero gets the identical markup without importing any editor state. That is the point of
 * this component: the hero previously carried its own cut-down renderer that only knew about
 * karaoke highlighting, so the newer templates (stagger, typewriter, free) could not be shown
 * there honestly.
 *
 * `getCurrentTime()` must return the video clock in seconds, already adjusted for any sync
 * offset. It is read inside ref callbacks as each span mounts, which is how a word's CSS
 * animation gets its negative delay; a plain `currentTime` prop would be stale by then.
 */
const CaptionContent = ({
  segment,
  segIndex,
  style,
  layout,
  activeWordIndex,
  litWordIndex = activeWordIndex,
  isPlaying,
  scale,
  videoHeight = 1080,
  hasDevanagari = false,
  geometry,
  getCurrentTime,
  toSeconds = defaultToSeconds,
  decorations = {},
}) => {
  const { wordDraft = null, selectedWordIndex = null, frameHover = false, wordIndexAttr = false } = decorations;
  if (!segment || !layout) return null;

  const draftFor = (i) => (wordDraft && wordDraft.index === i ? wordDraft.patch : null);
  // A word's own colour wins, else the template's hero/accent colour for that word, else nothing.
  const flowRuleColor = (i) => {
    const r = layout.wordRules;
    if (!r) return null;
    return (r.heroes[i] && r.heroColor) || (r.accents[i] && r.accentColor) || null;
  };
  const idAttr = (i) => (wordIndexAttr ? { 'data-word-index': i } : {});
  // Filled box behind the spoken word (same geometry as the burn's drawn rect); null without it.
  // Per word: a Devanagari word's box spans its own ink (marks above, matras below).
  const pillStyleFor = (word) => activeWordPillStyle(style, scale, { hasDevanagari, word });
  const pillAngle = activeWordPillAngle(style);
  // Devanagari pills: the box paints this much higher and the glyphs this much lower inside it, so
  // the box sits on the word the way the burn draws it while the word keeps its baseline.
  const pillLiftFor = (word) => activeWordPillLift(style, scale, { hasDevanagari, word });

  // ── Typewriter (animation: 'typewriter') ──
  // Each character animates in at its own time (negative delay for letters already typed); its
  // ::before is the grey typing block, shown until the next letter appears. The span key changes
  // with the spoken word, so each word re-syncs to the video.
  const isTypewriter = style.animation === 'typewriter' && style.layout !== 'free';
  const typeTimeline = isTypewriter && isPlaying && segment.words?.length
    ? typewriterTimeline(segment.words.map((w) => ({
      text: transformText(w.word, style.textTransform || 'none'),
      start: toSeconds(w.start),
      end: toSeconds(w.end),
    })))
    : null;
  const startTypewriterChar = (el, at, boxEnd) => {
    if (!el || el.dataset.twStarted) return;
    el.dataset.twStarted = '1';
    const now = getCurrentTime ? getCurrentTime() : 0;
    el.style.setProperty('--tw-at', `${Math.round((at - now) * 1000)}ms`);
    el.style.setProperty('--tw-box', `${Math.max(0, Math.round((boxEnd - at) * 1000))}ms`);
  };
  const typewriterChars = (i) => {
    const chars = typeTimeline?.[i] || [];
    // A cursive word (Urdu) cannot be split into letter spans without losing its joins, so it
    // types in whole, on its first letter's cue; the burn still reveals it letter by letter.
    if (layout.rtl && chars.length > 1 && /[؀-ۿ]/.test(segment.words[i]?.word || '')) {
      const first = chars[0];
      const last = chars[chars.length - 1];
      return [
        <span key={`${segIndex}:${activeWordIndex}:0`} className="tw-char" ref={(el) => startTypewriterChar(el, first.at, last.boxEnd)}>
          {chars.map((ch) => ch.c).join('')}
        </span>,
      ];
    }
    return chars.map(({ c, at, boxEnd }, k) => (
      <span key={`${segIndex}:${activeWordIndex}:${k}`} className="tw-char" ref={(el) => startTypewriterChar(el, at, boxEnd)}>
        {c}
      </span>
    ));
  };
  // Right-to-left captions (Urdu): an Urdu word's span runs right to left on its own (its
  // punctuation on the left, like the burn), a Latin word or number keeps left to right (its comma
  // on the right, as libass resolves it inside a Latin run), and lines place their words in the
  // shared layout's visual order (layout.visualLines, the word-level bidi order libass draws).
  const rtlWordFor = (text) => (layout.rtl && /[RA]/.test(wordBidiClass(text)) ? { direction: 'rtl' } : {});
  const rtlRow = layout.rtl ? { direction: 'rtl' } : {};
  const drawnOrder = (li, line) => (layout.visualLines ? layout.visualLines[li] : line);

  // ── Stagger (layout: 'stagger') ──
  // Lines sit exactly where the shared layout puts them for the burn (left edge plus vertical
  // centre, video px converted to preview px). While playing, each word fades in from its
  // spoken time.
  const startStaggerWord = (el, wordStart) => {
    if (!el || el.dataset.swStarted) return;
    el.dataset.swStarted = '1';
    const now = getCurrentTime ? getCurrentTime() : 0;
    el.style.setProperty('--sw-at', `${Math.round((wordStart - now) * 1000)}ms`);
  };

  const renderStagger = () => {
    const st = layout.stagger;
    const px = (scale * 1080) / (videoHeight || 1080);
    // Hero words draw in the hero face: weight always, family and italic when the template sets them.
    const heroFace = st.heroFace ? {
      fontWeight: st.heroFace.weight,
      ...(style.heroFontFamily ? { fontFamily: `${st.heroFace.indicFamily ? `'${st.heroFace.indicFamily}', ` : ''}'${st.heroFace.family}', ${style.fontFamily}, serif` } : {}),
      ...(st.heroFace.italic ? { fontStyle: 'italic' } : {}),
    } : null;
    if (st.tiles) return (
      <div className="relative" style={{ width: `${st.blockWidth * px}px`, height: `${st.blockHeight * px}px` }}>
        {st.tiles.map((tile) => {
          const i = tile.index;
          const w = segment.words[i];
          const d = draftFor(i);
          const picked = selectedWordIndex === i;
          const future = i > activeWordIndex;
          const ownColor = d?.color !== undefined ? d.color : w.color;
          const active = i === litWordIndex && !future && !!style.activeWordPill;
          return <span key={i} {...idAttr(i)} style={{
            ...captionWordTileStyle(tile, st, px, active),
            ...(heroFace && layout.heroes[i] ? heroFace : {}),
            color: ownColor || flowRuleColor(i) || style.color,
            opacity: future ? ((!isPlaying && (frameHover || picked)) ? 0.3 : 0) : active ? 1 : tile.pastOpacity,
            outline: picked ? '1.5px solid #818CF8' : undefined,
          }}>
            {active && <span aria-hidden="true" style={{ position: 'absolute', inset: 0, borderRadius: `${tile.radius * px}px`, backgroundColor: style.activeWordPillColor || '#242621' }} />}
            <span style={{ position: 'relative', top: `${captionWordTileDrop(style, tile, px, { hasDevanagari, active })}px`, textShadow: geometry.wordDropShadow, ...rtlWordFor(layout.texts[i]) }}>{layout.texts[i]}</span>
          </span>;
        })}
      </div>
    );
    return (
      <div className="relative" style={{ width: `${st.blockWidth * px}px`, height: `${st.blockHeight * px}px`, lineHeight: 1 }}>
        {/* heroBehind: the hero drawn big behind the text (painted first), centred on the block, in
            its own face and optional sheen gradient. With line-height 1 its capitals centre on `y`. */}
        {(st.behind || []).map((b) => {
          const i = b.index;
          const w = segment.words[i];
          const hf = st.heroFace;
          if (!w || !hf) return null;
          const d = draftFor(i);
          const ownColor = d && d.color !== undefined ? d.color : (typeof w.color === 'string' ? w.color : null);
          const isPicked = selectedWordIndex === i;
          const hidden = !isPlaying && i > activeWordIndex && !isPicked;
          const fill = hf.gradient && !ownColor
            ? { backgroundImage: `linear-gradient(90deg, ${hf.gradient.join(', ')})`, WebkitBackgroundClip: 'text', backgroundClip: 'text', color: 'transparent' }
            : { color: ownColor || flowRuleColor(i) || style.color };
          return (
            <span
              key={`behind:${isPlaying ? `${segIndex}:${activeWordIndex}` : 'paused'}:${i}`}
              {...idAttr(i)}
              className={isPlaying ? 'sw-word absolute whitespace-nowrap' : 'absolute whitespace-nowrap'}
              ref={isPlaying ? (el) => startStaggerWord(el, toSeconds(w.start)) : undefined}
              style={{
                left: `${(b.x - st.blockLeft + b.width / 2) * px}px`,
                top: `${(b.y - st.blockTop) * px}px`,
                transform: `translate(-50%, -50%) scaleX(${hf.scaleX || 1})`,
                fontFamily: `${hf.indicFamily ? `'${hf.indicFamily}', ` : ''}'${hf.family}', ${style.fontFamily}, sans-serif`,
                fontWeight: hf.weight,
                fontStyle: 'normal',
                fontSize: `${b.scale}em`,
                lineHeight: 1,
                textTransform: 'none',
                textShadow: 'none',
                WebkitTextStroke: '0px transparent',
                ...fill,
                ...rtlWordFor(layout.texts[i]),
                outline: isPicked ? '1.5px solid #818CF8' : undefined,
                opacity: isPlaying ? undefined : (hidden ? ((frameHover || selectedWordIndex !== null) ? 0.3 : 0) : 1),
              }}
            >
              {layout.texts[i]}
            </span>
          );
        })}
        {st.lines.map((ln, li) => (
          <div
            key={li}
            className="absolute whitespace-nowrap"
            style={{ left: `${(ln.x - st.blockLeft) * px}px`, top: `${(ln.y - st.blockTop) * px}px`, transform: 'translateY(-50%)' }}
          >
            {drawnOrder(li, ln.words).map((i, k) => {
              const w = segment.words[i];
              const d = draftFor(i);
              const ownColor = d && d.color !== undefined ? d.color : (typeof w.color === 'string' ? w.color : null);
              const wordScale = d && d.scale !== undefined
                ? fitScaleToWidth(clampWordScale(d.scale), layout.baseWidths[i], layout.availableWidth)
                : layout.wordScales[i];
              const isPicked = selectedWordIndex === i;
              const hidden = !isPlaying && i > activeWordIndex && !isPicked;
              return (
                <React.Fragment key={i}>
                  {k > 0 ? ' ' : ''}
                  <span
                    key={isPlaying ? `${segIndex}:${activeWordIndex}:${i}` : `paused:${i}`}
                    {...idAttr(i)}
                    className={isPlaying ? (st.blurIn ? 'sw-word sw-blur' : 'sw-word') : undefined}
                    ref={isPlaying ? (el) => startStaggerWord(el, toSeconds(w.start)) : undefined}
                    style={{
                      display: 'inline-block',
                      ...rtlWordFor(layout.texts[i]),
                      // Texts already carry the caption's case (and the hero's own), so no CSS transform.
                      textTransform: 'none',
                      ...(heroFace && layout.heroes[i] ? heroFace : {}),
                      color: ownColor || flowRuleColor(i) || style.color,
                      fontSize: wordScale !== 1 ? `${wordScale}em` : undefined,
                      textShadow: geometry.wordDropShadow,
                      outline: isPicked ? '1.5px solid #818CF8' : undefined,
                      outlineOffset: '3px',
                      opacity: isPlaying ? undefined : (hidden ? ((frameHover || selectedWordIndex !== null) ? 0.3 : 0) : 1),
                    }}
                  >
                    {layout.texts[i]}
                  </span>
                </React.Fragment>
              );
            })}
          </div>
        ))}
      </div>
    );
  };

  // ── Karaoke flow: words grouped into the SAME lines the burn produces, so preview and export
  // cannot wrap apart. Hero and accent colours come from the transcript-wide word rules. ──
  const renderKaraoke = () => (
    <div
      className="flex flex-col"
      style={{
        alignItems: style.textAlign === 'left' ? 'flex-start' : style.textAlign === 'right' ? 'flex-end' : 'center',
        lineHeight: style.lineHeight || 'normal',
      }}
    >
      {layout.lines.map((line, li) => {
        const spans = drawnOrder(li, line).map((i) => {
          const w = segment.words[i];
          // activeWordIndex = last word that has started (past/future styling); litWordIndex = the
          // word whose highlight is on right now (-1 in the pause after a word lets go).
          const isActive = i === litWordIndex;
          const isBubble = !!style.activeWordPill;
          const pillStyle = isBubble && isActive ? pillStyleFor(w.word) : null;
          const pillLift = isBubble && isActive ? pillLiftFor(w.word) : 0;
          const isFuture = i > activeWordIndex;
          const shouldBlur = style.inactiveBlur && (style.blurFutureOnly ? isFuture : !isActive);
          if (style.additiveMode && isFuture) return null;

          let wordColor;
          if (style.highlightPast) {
            // Fill mode: past and active take the primary colour (not highlightColor) so the
            // filled text stays one colour; future words take the secondary.
            wordColor = isFuture ? (style.secondaryColor || '#AAAAAA') : style.color;
          } else {
            wordColor = isActive ? (style.highlightColor || style.color) : (style.secondaryColor || style.color);
          }

          // Per-word overrides (same rule as the burn): own colour wins except for not-yet-reached
          // words in fill styles; own size multiplies the caption size.
          const d = draftFor(i);
          const ownColor = d && d.color !== undefined ? d.color : (typeof w.color === 'string' ? w.color : null);
          const ownScale = d && d.scale !== undefined
            ? fitScaleToWidth(clampWordScale(d.scale), layout.baseWidths[i], layout.availableWidth)
            : layout.wordScales[i];
          const ruleColor = flowRuleColor(i);
          if ((ownColor || ruleColor) && !(style.highlightPast && isFuture)) wordColor = ownColor || ruleColor;
          const isPicked = selectedWordIndex === i;
          // Typewriter while paused: only words already reached are on screen; the rest show
          // faintly while editing so they can still be clicked.
          const typeHidden = isTypewriter && !isPlaying && i > activeWordIndex && !isPicked;

          return (
            <span
              key={i}
              {...idAttr(i)}
              style={{
                color: wordColor,
                fontSize: ownScale !== 1 ? `${ownScale}em` : undefined,
                outline: isPicked ? '1.5px solid #818CF8' : undefined,
                outlineOffset: '3px',
                ...((isBubble && isActive && pillStyle) ? pillStyle : { backgroundColor: 'transparent', padding: '0', borderRadius: '0' }),
                ...((isBubble && isActive && pillLift) ? { position: 'relative', top: `${-pillLift}px` } : {}),
                display: 'inline-block',
                ...rtlWordFor(w.word),
                fontWeight: (isActive && style.highlightFontWeight)
                  ? style.highlightFontWeight
                  : (isActive && style.highlightBold !== undefined)
                    ? (style.highlightBold ? 'bold' : 'normal')
                    : (style.fontWeight || (style.bold ? 'bold' : 'normal')),
                fontStyle: (isActive && style.highlightItalic) ? 'italic' : (style.italic ? 'italic' : 'normal'),
                fontFamily: (isActive && style.highlightFontFamily) ? style.highlightFontFamily : 'inherit',
                textShadow: (isActive && style.highlightGlow)
                  ? `${geometry.outlineRing ? `${geometry.outlineRing}, ` : ''}0 0 15px ${style.glowColor || 'rgba(255,255,255,0.8)'}`
                  : (isBubble && isActive && pillStyle) ? pillStyle.textShadow : geometry.wordDropShadow,
                // Blur is a design-space px value like fontSize, so it scales with the preview
                // (and with the burn's videoHeight/1080). Unscaled it blurred harder the smaller
                // the player got and never matched the export.
                filter: shouldBlur ? `blur(${style.inactiveBlur * scale}px)` : 'none',
                opacity: typeHidden ? ((frameHover || selectedWordIndex !== null) ? 0.3 : 0) : (shouldBlur ? 0.6 : 1),
                transform: `${(isActive && style.activeScale) ? `scale(${style.activeScale}) translateY(-2%)` : 'scale(1) translateY(0)'}${(isBubble && isActive && pillAngle) ? ` rotate(${pillAngle}deg)` : ''}`,
                transition: 'all 0.2s cubic-bezier(0.175, 0.885, 0.32, 1.275)',
              }}
            >
              {(isBubble && isActive && pillLift)
                ? <span style={{ position: 'relative', top: `${pillLift}px` }}>{typeTimeline ? typewriterChars(i) : w.word}</span>
                : (typeTimeline ? typewriterChars(i) : w.word)}
            </span>
          );
        }).filter(Boolean);
        if (spans.length === 0) return null;
        return (
          <div
            key={li}
            className="flex items-baseline whitespace-nowrap"
            style={{
              gap: geometry.isLinesBox ? '0.25em' : '0.35em',
              ...(geometry.linePillStyle || {}),
              ...(geometry.isLinesBox && li > 0 ? { marginTop: `${geometry.linePillGap}px` } : {}),
            }}
          >
            {spans}
          </div>
        );
      })}
    </div>
  );

  // ── Plain text: spans only when words align 1:1 with the text (the burn uses the same test). ──
  const renderPlain = () => {
    const text = segment.text || '';
    const tokens = text.trim().split(/\s+/).filter(Boolean);
    const ws = segment.words;
    const aligned = Array.isArray(ws) && ws.length === tokens.length && !text.includes('\n');
    const wordSpans = !aligned ? null : tokens.map((tok, i) => {
        const d = draftFor(i);
        const ownColor = (d && d.color !== undefined ? d.color : (typeof ws[i].color === 'string' ? ws[i].color : null)) || flowRuleColor(i);
        const requested = d && d.scale !== undefined ? clampWordScale(d.scale) : clampWordScale(ws[i] && ws[i].scale);
        const ownScale = ws[i] && (d?.scale !== undefined || (ws[i].scale !== undefined && ws[i].scale !== null))
          ? fitScaleToWidth(requested, layout.baseWidths[i], layout.availableWidth)
          : (layout.wordScales?.[i] ?? requested);
        return (
          <React.Fragment key={i}>
            <span
              {...idAttr(i)}
              style={{
                color: ownColor || undefined,
                fontSize: ownScale !== 1 ? `${ownScale}em` : undefined,
                outline: selectedWordIndex === i ? '1.5px solid #818CF8' : undefined,
                outlineOffset: '3px',
              }}
            >{tok}</span>
          </React.Fragment>
        );
      });
    // Words are grouped into the SAME lines the burn produces (shared layoutCaption, like the
    // karaoke flow above), so the export cannot wrap apart from the preview; each line is a
    // nowrap row. Text that does not line up with its words wraps as a paragraph.
    // Plain spans are inline, so a right-to-left row lets the browser order the whole line by
    // the bidi algorithm (reading order in, like the burn's whole-line event).
    const body = wordSpans && Array.isArray(layout.lines)
      ? layout.lines.map((line, li) => (
        <div key={li} style={{ whiteSpace: 'nowrap', ...rtlRow }}>
          {line.map((i, k) => <React.Fragment key={i}>{k > 0 ? ' ' : ''}{wordSpans[i]}</React.Fragment>)}
        </div>
      ))
      : (wordSpans || text);
    return (
      <div style={{
        textTransform: style.textTransform || 'none',
        fontWeight: style.fontWeight || (style.bold ? 'bold' : 'normal'),
        lineHeight: style.lineHeight || '1.2',
        ...(!wordSpans ? rtlRow : {}),
      }}>{body}</div>
    );
  };

  if (layout.stagger) return renderStagger();
  if (style.karaokeMode) return renderKaraoke();
  return renderPlain();
};

/**
 * Playful templates (layout: 'free') place every word individually across the whole frame, so
 * they render outside the caption box rather than inside it.
 *
 * This is the read-only half. The editor keeps its own copy in VideoPlayer because two thirds of
 * that code is drag handling, rotate/scale handles, snap guides and ghost markers for unplaced
 * words, and lifting those out would put word dragging (the template's headline feature) at risk
 * for no gain. If that ever gets refactored, this is the piece it should collapse into.
 */
export const CaptionFreeWords = ({
  segment,
  segIndex,
  style,
  layout,
  activeWordIndex,
  litWordIndex = activeWordIndex,
  scale,
  wordStyle,
}) => {
  if (!segment || !layout?.placed) return null;
  return (
    <div className="absolute inset-0 pointer-events-none">
      {layout.placed.map((pw) => {
        // 'stack' keeps earlier words on screen; 'pop' shows one word at a time, only while its
        // highlight is on (litWordIndex: the shared hold rule, see wordHighlightEnd).
        const spoken = layout.hold === 'stack' ? pw.index <= activeWordIndex : pw.index === litWordIndex;
        if (!spoken) return null;
        // Auto (null) falls back to the template's colour for this word: the hero colour for heroes.
        const ruleColor = pw.hero ? (layout.rules?.heroColor || null) : null;
        return (
          <span
            key={`${segIndex}-${pw.index}`}
            className="absolute whitespace-nowrap select-none word-pop-in"
            style={{
              ...wordStyle,
              left: `${pw.fx * 100}%`,
              top: `${pw.fy * 100}%`,
              '--rot': `${pw.rotation || 0}deg`,
              transform: `translate(-50%, -50%) rotate(${pw.rotation || 0}deg)`,
              fontSize: `${(style.fontSize || 48) * scale * pw.scale}px`,
              color: pw.color || ruleColor || style.color,
              // An Urdu word reads right to left on its own (its punctuation on the left, as the burn draws it).
              ...(layout.rtl && /[RA]/.test(wordBidiClass(segment.words[pw.index]?.word)) ? { direction: 'rtl' } : {}),
            }}
          >
            {segment.words[pw.index]?.word}
          </span>
        );
      })}
    </div>
  );
};

export default CaptionContent;
